/**
 * PATH: packages/bills/src/components/BillsBulkBar.tsx
 * FILE_ID: BILLS-PKG-CMP-BULK-BAR-001
 * PURPOSE: Multi-select action bar for the bills queue — retry read /
 *          restore / dismiss, each with a preview of exactly what will
 *          change (and what will be skipped, and why) before it runs.
 *
 * WHY: Nobody clears 636 blocked rows one at a time (design premise
 *      "bulk action is mandatory"). The preview is the safety: a bulk
 *      re-read can never silently include a row a person has approved
 *      or corrected — those are listed as skipped, by name, before the
 *      operator confirms (see planBulkAction in billsQueueModel.ts).
 *
 * DEPENDENCIES: react, lucide-react, @quotech/ui, ./billsQueueModel.
 */

import { useMemo, useState } from 'react';
import { RotateCcw, ShieldAlert, Undo2, X, XCircle } from 'lucide-react';
import { Button } from '@quotech/ui';
import type { BillListRow } from '../types';
import {
  BULK_ACTION_COPY,
  planBulkAction,
  type BulkAction,
} from './billsQueueModel';

interface Props {
  selectedRows: BillListRow[];
  busy: boolean;
  onClear: () => void;
  /** Runs the action on the previewed-eligible rows only. */
  onRun: (action: BulkAction, rows: BillListRow[]) => Promise<void> | void;
}

const PREVIEW_CAP = 5;

export function BillsBulkBar({ selectedRows, busy, onClear, onRun }: Props) {
  const [pending, setPending] = useState<BulkAction | null>(null);
  const plan = useMemo(
    () => (pending ? planBulkAction(pending, selectedRows) : null),
    [pending, selectedRows],
  );

  if (selectedRows.length === 0) return null;

  const confirm = async () => {
    if (!plan || plan.eligible.length === 0) return;
    await onRun(plan.action, plan.eligible);
    setPending(null);
    onClear();
  };

  return (
    <div className="shrink-0 border-t border-border bg-card shadow-[0_-1px_2px_0_rgb(0_0_0/0.06)]">
      {/* Selection summary + action buttons */}
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <span className="text-xs font-medium tabular-nums text-foreground">
          {selectedRows.length} selected
        </span>
        <span className="flex items-center gap-1">
          <BulkButton
            icon={<RotateCcw className="h-3 w-3" aria-hidden />}
            label={BULK_ACTION_COPY.retry.label}
            active={pending === 'retry'}
            disabled={busy}
            onClick={() => setPending((p) => (p === 'retry' ? null : 'retry'))}
          />
          <BulkButton
            icon={<Undo2 className="h-3 w-3" aria-hidden />}
            label={BULK_ACTION_COPY.restore.label}
            active={pending === 'restore'}
            disabled={busy}
            onClick={() => setPending((p) => (p === 'restore' ? null : 'restore'))}
          />
          <BulkButton
            icon={<XCircle className="h-3 w-3" aria-hidden />}
            label={BULK_ACTION_COPY.dismiss.label}
            active={pending === 'dismiss'}
            disabled={busy}
            onClick={() => setPending((p) => (p === 'dismiss' ? null : 'dismiss'))}
          />
          <button
            type="button"
            onClick={() => {
              setPending(null);
              onClear();
            }}
            aria-label="Clear selection"
            title="Clear selection (Esc)"
            className="ml-1 flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </span>
      </div>

      {/* Preview — exactly what will change, and what won't, before it runs. */}
      {plan && (
        <div className="space-y-2 border-t border-border/60 px-3 py-2 text-xs">
          <p className="text-muted-foreground">{BULK_ACTION_COPY[plan.action].detail}</p>
          <p className="font-medium text-foreground">
            Will {BULK_ACTION_COPY[plan.action].verb.toLowerCase()}{' '}
            <span className="tabular-nums">{plan.eligible.length}</span>{' '}
            {plan.eligible.length === 1 ? 'bill' : 'bills'}
            {plan.skipped.length > 0 && (
              <>
                {' — '}
                <span className="tabular-nums">{plan.skipped.length}</span> skipped
              </>
            )}
          </p>
          {plan.skipped.length > 0 && (
            <ul className="space-y-0.5">
              {plan.skipped.slice(0, PREVIEW_CAP).map(({ row, reason }) => (
                <li key={row.id} className="flex items-start gap-1 text-muted-foreground">
                  <ShieldAlert className="mt-0.5 h-3 w-3 shrink-0 text-amber-600" aria-hidden />
                  <span className="min-w-0 truncate">
                    <span className="font-medium text-foreground">
                      {row.vendor_name || 'Unknown vendor'}
                    </span>
                    {row.invoice_number ? ` · ${row.invoice_number}` : ''} — {reason}
                  </span>
                </li>
              ))}
              {plan.skipped.length > PREVIEW_CAP && (
                <li className="text-muted-foreground">
                  …and {plan.skipped.length - PREVIEW_CAP} more skipped
                </li>
              )}
            </ul>
          )}
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => setPending(null)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="h-7 px-3 text-xs"
              onClick={() => void confirm()}
              disabled={busy || plan.eligible.length === 0}
            >
              {busy
                ? 'Working…'
                : `${BULK_ACTION_COPY[plan.action].verb} ${plan.eligible.length}`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function BulkButton({
  icon,
  label,
  active,
  disabled,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={
        'flex h-6 items-center gap-1 rounded border px-1.5 text-[11px] font-medium transition-colors disabled:opacity-50 ' +
        (active
          ? 'border-primary/40 bg-primary/10 text-primary'
          : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground')
      }
    >
      {icon}
      {label}
    </button>
  );
}
